import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, FileText, FolderOpen, ExternalLink, Menu, X } from 'lucide-react';
import { parseMarkdown } from '../utils/markdown';
import { API_BASE_URL } from '../utils/api';

/** Inline markdown: bold, italic, code, images, links */
function InlineMarkdown({ text, onNavigate }) {
  const parts = [];
  const regex = /(!\[([^\]]*)\]\(([^)]+)\))|(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      parts.push(<img key={m.index} src={m[3]} alt={m[2]} className="inline-block max-w-full rounded" style={{ maxHeight: 200 }} />);
    }
    else if (m[4]) parts.push(<strong key={m.index} className="font-semibold text-zinc-100">{m[5]}</strong>);
    else if (m[6]) parts.push(<em key={m.index} className="italic text-zinc-300">{m[7]}</em>);
    else if (m[8]) parts.push(<code key={m.index} className="rounded bg-zinc-800 px-1.5 py-0.5 text-[0.8em] text-violet-300 font-mono">{m[9]}</code>);
    else if (m[10]) {
      const href = m[12];
      const label = m[11];
      // external link
      if (/^https?:\/\//.test(href)) {
        parts.push(
          <a key={m.index} href={href} target="_blank" rel="noopener noreferrer"
             className="text-violet-400 hover:text-violet-300 underline underline-offset-2 inline-flex items-center gap-0.5">
            {label}<ExternalLink size={11} className="inline" />
          </a>
        );
      } else {
        // internal wiki link
        parts.push(
          <button key={m.index} type="button"
                  onClick={() => onNavigate && onNavigate(href)}
                  className="text-violet-400 hover:text-violet-300 underline underline-offset-2 cursor-pointer bg-transparent border-none p-0 font-inherit text-inherit">
            {label}
          </button>
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function MarkdownRenderer({ markdown, onNavigate }) {
  const tokens = useMemo(() => parseMarkdown(markdown), [markdown]);

  return (
    <div className="docs-content space-y-4 text-sm leading-relaxed text-zinc-300">
      {tokens.map((tok, idx) => {
        switch (tok.type) {
          case 'heading': {
            const Tag = `h${tok.level}`;
            const sizes = {
              1: 'text-2xl font-bold text-zinc-100 mt-2 mb-3',
              2: 'text-xl font-semibold text-zinc-100 mt-6 mb-2',
              3: 'text-base font-semibold text-zinc-200 mt-4 mb-1',
              4: 'text-sm font-semibold text-zinc-200 mt-3 mb-1',
            };
            return <Tag key={idx} className={sizes[tok.level] || sizes[4]}><InlineMarkdown text={tok.text} onNavigate={onNavigate} /></Tag>;
          }
          case 'paragraph':
            return <p key={idx}><InlineMarkdown text={tok.text} onNavigate={onNavigate} /></p>;
          case 'code':
            return (
              <pre key={idx} className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900/80 p-4 text-xs font-mono text-zinc-300 leading-relaxed">
                <code>{tok.text}</code>
              </pre>
            );
          case 'ul':
            return (
              <ul key={idx} className="list-disc list-inside space-y-1 pl-1">
                {tok.items.map((item, j) => <li key={j}><InlineMarkdown text={item} onNavigate={onNavigate} /></li>)}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx} className="list-decimal list-inside space-y-1 pl-1">
                {tok.items.map((item, j) => <li key={j}><InlineMarkdown text={item} onNavigate={onNavigate} /></li>)}
              </ol>
            );
          case 'blockquote':
            return (
              <blockquote key={idx} className="border-l-2 border-violet-500/50 pl-4 italic text-zinc-400">
                <InlineMarkdown text={tok.text} onNavigate={onNavigate} />
              </blockquote>
            );
          case 'table':
            return (
              <div key={idx} className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      {tok.header.map((h, j) => (
                        <th key={j} className="px-3 py-2 text-left font-semibold text-zinc-200">
                          <InlineMarkdown text={h} onNavigate={onNavigate} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tok.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-zinc-800/60">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-2">
                            <InlineMarkdown text={cell} onNavigate={onNavigate} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'hr':
            return <hr key={idx} className="border-zinc-800 my-4" />;
          default:
            return null;
        }
      })}
    </div>
  );
}

/* ── DocsPage ─────────────────────────────────────────────────────────── */

const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/docs/docs-manifest.json`;
const DOCS_BASE = `${process.env.PUBLIC_URL || ''}/docs`;

function resolveSlug(currentSlug, href) {
  // relative link resolution
  if (href.startsWith('/')) return href.slice(1);
  const parts = currentSlug.split('/');
  parts.pop(); // remove current file name
  for (const seg of href.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

/** Flatten manifest pages into a list of { slug, title, group? } */
function flattenManifest(manifest) {
  const result = [];
  for (const entry of manifest.pages) {
    if (entry.group) {
      for (const p of entry.pages) {
        result.push({ ...p, group: entry.group });
      }
    } else {
      result.push(entry);
    }
  }
  return result;
}

function groupManifest(manifest) {
  const groups = [];
  for (const entry of manifest.pages) {
    if (entry.group) {
      groups.push({ type: 'group', label: entry.group, pages: entry.pages });
    } else {
      groups.push({ type: 'page', ...entry });
    }
  }
  return groups;
}

function resolveTemplateVars(text) {
  const now = new Date();
  const vars = {
    date: now.toLocaleDateString('ru-RU'),
    time: now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
    year: String(now.getFullYear()),
  };
  return text.replace(/\{\{(\w+)\}\}/g, (m, key) => vars[key] ?? m);
}

export default function DocsPage() {
  const [manifest, setManifest] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine teamId from session — non-default teams use database-backed docs
  const teamId = useMemo(() => {
    const isDefault = sessionStorage.getItem('flowcore_active_team_default');
    if (isDefault === '1') return null;
    return sessionStorage.getItem('flowcore_active_team') || null;
  }, []);

  // load manifest
  useEffect(() => {
    (async () => {
      try {
        let data;
        if (teamId) {
          // Load from database API
          const token = sessionStorage.getItem('flowcore_session_token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await fetch(`${API_BASE_URL}/api/team-docs/${teamId}`, { headers });
          if (!res.ok) throw new Error('Failed to load team docs');
          data = await res.json();
        } else {
          // Load static manifest
          const res = await fetch(MANIFEST_URL);
          if (!res.ok) throw new Error('Failed to load docs manifest');
          data = await res.json();
        }
        setManifest(data);
        // check hash for slug, fallback to default
        const hash = window.location.hash.slice(1);
        const flat = flattenManifest(data);
        const initial = flat.find(p => p.slug === hash)?.slug || data.defaultPage || flat[0]?.slug || '';
        setActiveSlug(initial);
        // expand all groups initially
        const expanded = {};
        for (const entry of (data.pages || [])) {
          if (entry.group) expanded[entry.group] = true;
        }
        setExpandedGroups(expanded);
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    })();
  }, [teamId]);

  // load markdown for active slug
  useEffect(() => {
    if (!manifest) return;
    if (!activeSlug) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);

    if (teamId) {
      // Load from database API
      (async () => {
        try {
          const token = sessionStorage.getItem('flowcore_session_token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await fetch(`${API_BASE_URL}/api/team-docs/${teamId}/${activeSlug}`, { headers });
          if (!res.ok) throw new Error(`Doc "${activeSlug}" not found`);
          const data = await res.json();
          if (!cancelled) {
            setMarkdown(resolveTemplateVars(data.content || ''));
            setError(null);
          }
        } catch (e) {
          if (!cancelled) setError(e.message);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    const flat = flattenManifest(manifest);
    const page = flat.find(p => p.slug === activeSlug);
    if (!page) {
      setError(`Page "${activeSlug}" not found`);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${DOCS_BASE}/${page.file}`);
        if (!res.ok) throw new Error(`Failed to load ${page.file}`);
        const text = await res.text();
        if (!cancelled) {
          setMarkdown(resolveTemplateVars(text));
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [manifest, activeSlug, teamId]);

  // update hash on navigation
  useEffect(() => {
    if (activeSlug) window.location.hash = activeSlug;
  }, [activeSlug]);

  const navigate = useCallback((href) => {
    const slug = resolveSlug(activeSlug, href);
    setActiveSlug(slug);
    setSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const toggleGroup = useCallback((group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  }, []);

  const sidebarItems = useMemo(() => manifest ? groupManifest(manifest) : [], [manifest]);

  const activeTitle = useMemo(() => {
    if (!manifest || !activeSlug) return '';
    const flat = flattenManifest(manifest);
    return flat.find(p => p.slug === activeSlug)?.title || '';
  }, [manifest, activeSlug]);

  const selectPage = useCallback((slug) => {
    setActiveSlug(slug);
    setSidebarOpen(false);
  }, []);

  const sidebarContent = (
    <>
      <div className="border-b border-zinc-800/90 px-3 py-2.5 flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-violet-400 shrink-0" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 leading-snug">
            {manifest?.title || 'Docs'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="md:hidden flex items-center justify-center rounded p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
      <nav className="vr-scroll-y min-h-0 flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {sidebarItems.map((item, idx) => {
          if (item.type === 'group') {
            const open = expandedGroups[item.label];
            return (
              <div key={idx}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.label)}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <ChevronRight
                    size={14}
                    className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
                  />
                  <FolderOpen size={14} className="shrink-0" />
                  <span>{item.label}</span>
                </button>
                {open && (
                  <div className="ml-3 space-y-0.5">
                    {item.pages.map((page) => {
                      const active = activeSlug === page.slug;
                      return (
                        <button
                          key={page.slug}
                          type="button"
                          onClick={() => selectPage(page.slug)}
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors min-h-[2.25rem] ${
                            active
                              ? 'border border-violet-500/35 bg-violet-500/15 text-violet-200'
                              : 'border border-transparent text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-200'
                          }`}
                        >
                          <FileText size={14} className="shrink-0" />
                          <span className="min-w-0 truncate leading-snug">{page.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          // top-level page
          const active = activeSlug === item.slug;
          return (
            <button
              key={item.slug}
              type="button"
              onClick={() => selectPage(item.slug)}
              className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors min-h-[2.25rem] ${
                active
                  ? 'border border-violet-500/35 bg-violet-500/15 text-violet-200'
                  : 'border border-transparent text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-200'
              }`}
            >
              <FileText size={14} className="shrink-0" />
              <span className="min-w-0 truncate leading-snug">{item.title}</span>
            </button>
          );
        })}
      </nav>
      <div className="border-t border-zinc-800/90 px-1.5 py-1">
        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium border border-transparent text-zinc-500 hover:bg-zinc-800/80 hover:text-zinc-300 transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={2} className="shrink-0" />
          <span className="min-w-0 leading-snug">Dashboard</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen w-full overflow-hidden bg-zinc-950 text-zinc-200">
      {/* mobile header */}
      <div className="fixed top-0 left-0 right-0 z-30 flex md:hidden items-center gap-2 border-b border-zinc-800/90 bg-zinc-900/95 backdrop-blur px-3 py-2">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="flex items-center justify-center rounded p-1 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        <BookOpen size={14} className="text-violet-400" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Docs</span>
      </div>

      {/* mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* sidebar — desktop: static, mobile: overlay */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-zinc-800/90 bg-zinc-900
        transition-transform duration-200 ease-in-out
        md:static md:translate-x-0 md:shrink-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {sidebarContent}
      </aside>

      {/* content */}
      <main className="vr-scroll-y min-h-0 min-w-0 flex-1 overflow-y-auto pt-11 md:pt-0">
        <div className="mx-auto w-full max-w-3xl px-5 md:px-8 py-6 md:py-8">
          {loading && (
            <div className="flex items-center gap-2 text-zinc-500 text-sm py-12">
              <div className="h-4 w-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
              Loading…
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-800/50 bg-red-900/20 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {!loading && !error && (
            <MarkdownRenderer markdown={markdown} onNavigate={navigate} />
          )}
        </div>
      </main>
    </div>
  );
}
